import moment from 'moment';
import _ from 'lodash';
import { flow, groupBy, sortBy, uniq, map } from 'lodash/fp';

import * as errors from './errors';

export function isValidDate(date) {
  return moment(date, 'YYYY-MM-DD', true).isValid();
}

export function createWeek(year, week) {
  return {
    year,
    week,
  };
}

/**
 * Returns a canonically formatted date
 * For instance, Wed Jul 20 2016 15:13:46 GMT+0200 (CEST) => 2016-07-20
 * @param date
 */
export function getDate(date) {
  const dateMoment = moment(new Date(date));
  return dateMoment.isValid() ? dateMoment.format('YYYY-MM-DD') : '';
}

/**
 * Returns getDate formatted date strings for all dates in the week containing date
 * @param date valid date formatted string
 * @returns {Array} getDate formatted date strings for all of the dates in that week
 */
export function getDatesInWeekWithDate(date) {
  const momentDate = moment(date);
  const dates = [];

  for (let dayOfWeek = 1; dayOfWeek <= 7; dayOfWeek += 1) {
    dates.push(getDate(momentDate.isoWeekday(dayOfWeek).toString()));
  }
  return dates;
}

/**
 * Returns getDate formatted date strings for all dates in the month containing date
 * @param date valid date formatted string
 * @returns {Array} getDate formatted date strings for all of the dates in that week
 */
export function getDatesInMonthWithDate(date) {
  const startDate = moment(date).startOf('month');
  const endDate = moment(date).endOf('month');
  const dates = [];

  for (let m = startDate; m.isBefore(endDate); m.add(1, 'days')) {
    dates.push(getDate(m.toString()));
  }

  return dates;
}

/**
 * Returns getDate formatted date strings for all dates in the week containing date
 * @param date valid date formatted string
 * @returns {Array} getDate formatted date strings for all of the dates in that week
 */
const getDatesBetweenDatesCache = {};
export function getDatesBetweenDates(fromDate, toDate) {
  const momentDate = moment(fromDate);
  const momentToDate = moment(toDate);
  if ((momentToDate.year() - momentDate.year()) > 100) {
    return [];
  }
  if (getDatesBetweenDatesCache.fromDate === fromDate
    && getDatesBetweenDatesCache.toDate === toDate) {
    return getDatesBetweenDatesCache.dates;
  }
  const diff = momentToDate.diff(momentDate, 'days');
  const dates = [];

  for (let i = 0; i <= diff; i += 1) {
    dates.push(getDate(momentDate.clone().add(i, 'days').format('YYYY-MM-DD').toString()));
  }

  getDatesBetweenDatesCache.fromDate = fromDate;
  getDatesBetweenDatesCache.toDate = toDate;
  getDatesBetweenDatesCache.dates = dates;
  return dates;
}

/**
 * All date strings are in  8601 formatted date strings
 */

export function dateInWeekIsSelected(selectedDates, dateInWeek) {
  const weekDates = getDatesInWeekWithDate(dateInWeek);
  return _.intersection(selectedDates, weekDates).length !== 0;
}

/**
 * For the dates provided, gets a unique list of all the dates in those associated weeks
 * @param dates
 * @returns {Array}
 */
export function getDatesInWeeksWithDates(dates) {
  dates.sort((date1, date2) => new Date(date1) - new Date(date2));

  let extendedDatesSet = [];
  dates.forEach((date) => {
    if (extendedDatesSet.includes(date)) {
      // This week's days are already added, no need to run again.
      return;
    }
    extendedDatesSet = _.concat(extendedDatesSet, getDatesInWeekWithDate(date));
  });

  return extendedDatesSet;
}

/**
 * Get canonically (see getDate()) formatted dates specified by year and week tuple.
 * @param year
 * @param week
 * @returns {Array}
 */
export function getDatesForWeek(year, week) {
  const dates = [];
  let currentDate = moment().isoWeekYear(year).isoWeek(week).isoWeekday(1).startOf('day');
  while (currentDate.isoWeek() == week) {
    dates.push(getDate(currentDate.toString()));
    currentDate = currentDate.add(1, 'day');
  }

  return dates;
}

export function getDatesForWeeks(weeks) {
  return _.flatten(weeks.map(week => getDatesForWeek(week.year, week.week)));
}

function parseStringToWeek(string) {
  const captureYearAndWeek = new RegExp('([0-9]{4}):([0-9]{1,2})');
  const matches = string.match(captureYearAndWeek);
  if (matches == null || matches.length !== 3) {
    return null;
  }

  try {
    const year = parseInt(matches[1], 10);
    const week = parseInt(matches[2], 10);
    return createWeek(year, week);
  } catch (error) {
    return null;
  }
}

function parseSpanToWeeks(string) {
  const subPatterns = string.split('-');
  const startWeek = parseStringToWeek(subPatterns[0]);
  const endWeek = parseStringToWeek(subPatterns[1]);

  if (startWeek === null) {
    throw new Error('Felaktig startvecka inmatad, formatet bör vara t. ex. 2018:31');
  } else if (endWeek === null) {
    throw new Error('Felaktig slutvecka inmatad, formatet bör vara t. ex. 2018:31');
  }

  let momentCounter = moment().isoWeekYear(startWeek.year).isoWeek(startWeek.week);
  const momentEnd = moment().isoWeekYear(endWeek.year).isoWeek(endWeek.week).endOf('day');

  const weeks = [];
  while (momentCounter.isSameOrBefore(momentEnd)) {
    const week = createWeek(momentCounter.isoWeekYear(), momentCounter.isoWeek());
    weeks.push(week);
    momentCounter = momentCounter.add(1, 'week');
  }
  return weeks;
}

// TODO: getWeeksFromString should throw exception something if invalid
export function getWeeksFromString(input) {
  const subPatterns = input.split(',');

  let weeks = [];
  weeks = _.flatten(subPatterns.map((subPattern) => {
    if (subPattern.indexOf('-') > 0) {
      return parseSpanToWeeks(subPattern);
    }

    return [parseStringToWeek(subPattern)];
  }),
  ).filter(week => week != null);

  return weeks;
}

export function selectSameDayOtherWeeks(sourceDate, dates) {
  const mSourceDate = moment(sourceDate);
  return _.filter(dates, (date) => {
    if (sourceDate === date) {
      return false;
    }
    return mSourceDate.diff(moment(date), 'days') % 7 === 0;
  });
}

export function selectWeekdaysSameWeek(sourceDate, dates) {
  const mSourceDate = moment(sourceDate);
  return _.filter(dates, (date) => {
    if (sourceDate === date) {
      return false;
    }

    const mDate = moment(date);
    return (
      mSourceDate.isoWeekYear() === mDate.isoWeekYear() &&
      mSourceDate.isoWeek() === mDate.isoWeek() &&
      (mDate.isoWeekday() >= 1 && mDate.isoWeekday() <= 5)
    );
  });
}

export function selectDaysSameWeek(sourceDate, dates) {
  const mSourceDate = moment(sourceDate);
  return _.filter(dates, (date) => {
    if (sourceDate === date) {
      return false;
    }

    const mDate = moment(date);
    return (
      mSourceDate.isoWeekYear() === mDate.isoWeekYear() &&
      mSourceDate.isoWeek() === mDate.isoWeek()
    );
  });
}

/**
 * Returns an array containing 8601 formatted date strings excluding the sourceDate.
 * @param sourceDate date string, or null to just select all weekdays
 * @param dates array of date strings
 * @returns {*}
 */
export function selectWeekdays(sourceDate, dates) {
  return _.filter(dates, (date) => {
    if (sourceDate === date) {
      return false;
    }

    const mDate = moment(date);
    return (mDate.isoWeekday() >= 1 && mDate.isoWeekday() <= 5);
  });
}

export function selectWeekendDays(sourceDate, dates) {
  return _.filter(dates, (date) => {
    if (sourceDate === date) {
      return false;
    }

    const mDate = moment(date);
    return (mDate.isoWeekday() === 6 || mDate.isoWeekday() === 7);
  });
}

export function selectAllDays(sourceDate, dates) {
  return _.filter(dates, date => !(sourceDate == date));
}

/**
 * Takes a string formatted like 17 or 1700 or 17:00 and outputs 17:00
 * @param string
 * @returns {*}
 */
export function stringToHourFormat(input) {
  const formats = ['HH', 'kk', 'HH:mm', 'kk:mm', 'HHmm', 'kkmm'];

  let result = `${input}`; // Implicit conversion from int to string

  if (!moment(input, formats, true).isValid()) {
    return '';
  }

  if (input.length == 2 && !isNaN(input)) {
    result = `${input}00`;
  }


  if (result[2] !== ':') {
    result = `${result.slice(0, 2)}:${result.slice(-2)}`;
  }

  return result;
}

/**
 * Takes a string formatted like 2016-06-17 or 20160617 and outputs 2016-06-17
 * @param input
 */
export function stringToDateFormat(input) {
  const formats = ['YYYYMMDD', 'YYYY-MM-DD'];

  if (!moment(input, formats, true).isValid()) {
    throw new errors.InvalidArgumentError('Date string was invalid');
  }

  return moment(input, formats, true).format('YYYY-MM-DD');
}


export function timeToInt(input) {
  let timeInteger;

  if (typeof (input) !== 'string') {
    throw new errors.InvalidArgumentError(`Expected parameter to be string, received ${input} with type ${typeof input}`);
  }

  // TODO: Rewrite so first and third if-statements can be put together
  if (input.length > 0 && input[0] == '-') {
    throw new errors.InvalidArgumentError('Time is negative');
  }

  timeInteger = parseInt(input.replace(/\D/g, ''));
  if (isNaN(timeInteger)) {
    throw new errors.InvalidArgumentError("Couldn't parse time to integer");
  }

  if (timeInteger < 0 || timeInteger > 2400) {
    throw new errors.InvalidArgumentError('Parsed time is invalid, hours can only be 00-24');
  }

  if (timeInteger % 100 > 59) {
    throw new errors.InvalidArgumentError('Parsed time is invalid, minutes can only be 00-59');
  }

  return timeInteger;
}


export function timesOverlap(t1start, t1end, t2start, t2end) {
  if (t1start === undefined
    || t1end === undefined
    || t2start === undefined
    || t2end === undefined) {
    return false;
  }

  if (t1start === ''
    || t1end === ''
    || t2start === ''
    || t2end === '') {
    return false;
  }

  try {
    t1start = timeToInt(t1start);
    t1end = timeToInt(t1end);
    t2start = timeToInt(t2start);
    t2end = timeToInt(t2end);
  } catch (error) {
    return false;
  }

  if ((t1start < t2end && t1end > t2start) || (t2start < t1end && t2end > t1start)) {
    return true;
  }

  return false;
}

function createTimeSpan(from, to) {
  return {
    from: _.padStart(`${from}`, 4, '0'),
    to: _.padStart(`${to}`, 4, '0'),
  };
}

/**
 * Subtracts t2 from t1
 * @param t1start
 * @param t1end
 * @param t2start
 * @param t2end
 * @returns {*}
 */
export function subtractTimes(t1start, t1end, t2start, t2end) {
  t1start = timeToInt(t1start);
  t1end = timeToInt(t1end);
  t2start = timeToInt(t2start);
  t2end = timeToInt(t2end);

  if (t2start <= t1start && t2end >= t1end) {
    // Subtracted all.
    return [];
  }

  if (t2start <= t1start && t2end < t1end) {
    return [createTimeSpan(t2end, t1end)];
  }

  if (t2start >= t1start && t2end < t1end) {
    return [
      createTimeSpan(t1start, t2start),
      createTimeSpan(t2end, t1end),
    ];
  }

  if (t2start >= t1start && t2end >= t1end) {
    return [
      createTimeSpan(t1start, t2start),
    ];
  }
}

/**
 * Returns a canonical week identifier for date
 * A week identifier is defined as a date corresponding to the beginning of the week
 * @param date date string
 */
export function getWeekIdentifier(date) {
  return moment(date).isoWeek() === 1 ? moment(date).endOf('isoWeek').format('YYYY-[W]WW') :
    moment(date).startOf('isoWeek').format('YYYY-[W]WW');
}

/**
 * Groups dates by week identifier
 * @param dates
 * @returns {*}
 */
export function groupDatesByWeekIdentifier(dates) {
  return groupBy(date => getWeekIdentifier(date), dates);
}

export function getWeeksBetweenDates(startDate, endDate) {
  let momentCounter = moment(startDate).startOf('isoWeek');
  const momentEnd = moment(endDate).startOf('isoWeek');

  const weeks = [];
  while (momentCounter.isSameOrBefore(momentEnd)) {
    weeks.push(getWeekIdentifier(momentCounter.toString()));
    momentCounter = momentCounter.add(1, 'week');
  }
  return weeks;
}

export function getWeekIdentifiersForDates(dates) {
  return flow(
    map(date => getWeekIdentifier(date)),
    uniq,
    sortBy(identifier => identifier),
  )(dates);
}


/**
 * Returns all dates belonging to weeks touching the specified month
 * @param month valid Date() formatted string for identifying a month
 * @returns {Array} array of getDate() formatted dates for all weeks touching the month
 */
export function getDatesForDisplayMonth(month) {
  const currentDate = moment(month).startOf('month').startOf('isoWeek');
  const lastDayOfLastWeekOfMonth = moment(month).add(1, 'month').date(0)
    .isoWeekday(7)
    .startOf('day');

  const dates = []; // Array containing moment objects of all of the dates.
  while (currentDate.isSameOrBefore(lastDayOfLastWeekOfMonth)) {
    dates.push(getDate(currentDate.toString()));
    currentDate.add(1, 'day');
  }

  return dates;
}


function minutesToString(minutes) {
  if (minutes === 0) {
    return '00';
  }

  const minuteString = String(minutes);
  if (minuteString.length === 1) {
    return `0${minuteString}`;
  }

  return String(minutes);
}


export function intToHourFormat(time) {
  if (typeof (time) !== 'number') {
    throw new errors.InvalidArgumentException('Excected an int');
  }

  if (time < 0
    || time > 2400) {
    throw new errors.InvalidArgumentException('Invalid time');
  }

  const hours = Math.floor(time / 100);
  const minutes = time % 100;

  if (time < 1000) {
    return `0${hours}:${minutesToString(minutes)}`;
  }


  return `${hours}:${minutesToString(minutes)}`;
}

export function repeatRepetitions(dates, repetitions, space = 0) {
  /**
   * Sort dates.
   * Take lowest. Take highest.
   * Add frequency to highest.
   * Add all dates shifted by frequencyStartWeek
   * Do until all occurrences are done.
   */

  if (repetitions < 1) {
    return dates;
  }

  const sortedDates = dates.sort((date1, date2) => new Date(date1) - new Date(date2));

  const lowestDate = _.first(sortedDates);
  const highestDate = _.last(sortedDates);

  const weeksSpanned = moment(highestDate).endOf('isoWeek').diff(moment(lowestDate).startOf('isoWeek'), 'weeks') + 1;

  const result = _.flattenDeep(
    _
      .range(1, repetitions + 1)
      .map(repetition => (repetition * (space + weeksSpanned)))
      .map(offsetWeeks => sortedDates.map(date => moment(date).add(offsetWeeks, 'weeks'))),
  ).map(m => getDate(m.toString()));

  return [...sortedDates, ...result];
}


export function repeatUntil(dates, untilDate, space = 0) {
  const sortedDates = dates.sort((date1, date2) => new Date(date1) - new Date(date2));

  const lowestDate = _.first(sortedDates);
  const highestDate = _.last(sortedDates);

  const weeksSpanned = moment(highestDate).endOf('isoWeek').diff(moment(lowestDate).startOf('isoWeek'), 'weeks') + 1;

  const untilDateMom = moment(untilDate);

  const repetitions = (untilDateMom.diff(highestDate, 'weeks') / (space + weeksSpanned)) + 1;
  return repeatRepetitions(dates, repetitions, space)
    .filter(date => (new Date(untilDate) - new Date(date)) >= 0);
}

export function dateToOrdinal(date) {
  const day = moment(date).day();
  return day === 0 ? 7 : day; // Note: momentjs uses 0-6, but backend uses 1-7. Adhering to backend.
}

export function datesToOrdinals(dates = []) {
  return dates.map(dateToOrdinal);
}

export function ordinalToDate(year, week, ordinal) {
  return getDate(
    moment()
      .year(year)
      .isoWeek(week)
      .day(ordinal)
      .toString(),
  );
}

export function ordinalsToDates(year, week, ordinals = []) {
  return ordinals.map(ordinal => ordinalToDate(year, week, ordinal));
}

export function countWeeks(dates = []) {
  return _.uniq(dates
    .map(date => moment(date))
    .map(dateMoment => `${dateMoment.year()}_${dateMoment.isoWeek()}`),
  ).length;
}

export function monthDates(from, to) {
  const mFrom = moment(from).startOf('month');
  const mTo = moment(to).startOf('month');

  const dates = [];
  for (let mIndex = mFrom.clone(); mIndex.isSameOrBefore(mTo); mIndex.add(1, 'month')) {
    dates.push(mIndex.format('YYYY-MM-DD'));
  }

  return dates;
}

/**
 * Takes dates and returns a human readable list of spans.
 * @param dates
 */
export function datesToSpans(dates) {
  return _.sortBy(dates, date => new Date(date)).reduce((spans, date) => {
    if (spans.length === 0) {
      return [
        ...spans,
        [date],
      ];
    }

    const lastSpan = spans[spans.length - 1];
    const comparisonDate = lastSpan[lastSpan.length - 1];
    if (moment(date).startOf('day').diff(moment(comparisonDate).startOf('day'), 'days') === 1) {
      return [
        ...spans.slice(0, spans.length - 1),
        [lastSpan[0], date],
      ];
    }

    return [
      ...spans,
      [date],
    ];
  }, []).map(span => (span.length === 1 ? span[0] : `${span[0]} - ${span[1]}`));
}
